import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';
import moment from 'moment';
import { ButtonV2 } from '@bbkAdminComponents/buttons';
import { useHasSubscription, useSubscriptionPlans } from '@bbkAdminUtils/plans';
import {
  useDaysToExpiration,
  usePlanPurchaseIntent,
  useSelectCompanySubscription,
} from '@bbkAdminRedux/rtkq/checkout.slice';
import { selectCompanyInternalName } from '@bbkAdminRedux/app/selectors';
import { useRoles } from '@bbkAdminUtils/roles';
import { Constants } from '@bbkShared/utils/constants/ui-constants';
import { cs } from '@bbkAdminUtils/css-utils';

export const usePurchaseFeature = () => {
  const { isEssentialsPlan } = useSubscriptionPlans();
  const expiration = useDaysToExpiration();

  const hasSubscription = useHasSubscription();
  const companyInternalName = useSelector(selectCompanyInternalName);
  const subscription = useSelectCompanySubscription(
    hasSubscription
      ? {
          company: companyInternalName,
        }
      : skipToken
  );

  const alertText = useMemo(() => {
    if (!expiration) return undefined;
    const { isTrial, endDate } = expiration;
    const daysToExpiry = endDate.diff(moment.utc(), 'days');
    if (isTrial) {
      return daysToExpiry < 0
        ? 'Trial expired'
        : `Expires on ${endDate.format('MMM D, YYYY')} UTC`;
    }
    if (isEssentialsPlan) {
      const usage = subscription
        ? ` (${subscription.usage}\\${subscription.price.included_session_limit})`
        : '';
      return `Essentials plan ${usage}`;
    }
    return undefined;
  }, [expiration, isEssentialsPlan, subscription]);

  return useMemo(() => ({ alertText }), [alertText]);
};

export const BottomAlertTab: React.FC = () => {
  const { hasBillingRole } = useRoles();
  const handlePlanPurchaseIntent = usePlanPurchaseIntent();
  const { alertText } = usePurchaseFeature();

  return (
    <div
      className={cs(
        'tw-flex tw-items-center',
        '-tw-mb-4 tw-px-2.5 tw-py-2',
        'tw-rounded-t tw-bg-cbrprimary-100 tw-text-xs tw-font-bold tw-text-cbrprimary-800'
      )}
    >
      <div className="tw-flex-1">{alertText}</div>
      {hasBillingRole && (
        <a
          href={Constants.PurchaseLink}
          rel="noreferrer noopener"
          target="_blank"
        >
          <ButtonV2
            onClick={() => handlePlanPurchaseIntent()}
            primary
            small
            css={{ marginLeft: 2 }}
          >
            Purchase
          </ButtonV2>
        </a>
      )}
    </div>
  );
};
